import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import { withRouter, RouterProps } from "../../../utils/withRouter";
import AuthLeftPanel from "../AuthLeftPanel";
import PrimaryButton from "../../../toolkit/primaryButton";
import PageTransition from "../../../toolkit/pageTransition";
import { serviceCaller, ServiceCallerProps } from "../../../Admin/composition";
import { executeResendVerification } from "../../../utils/fetch";
import { MobileLogo, AuthContentCol } from "../shared";

interface EmailSentPageProps extends RouterProps, ServiceCallerProps {}

const EmailSentPage: React.FC<EmailSentPageProps> = ({ navigate, location, config }) => {
  const [userEmail, setUserEmail] = useState<string>("");
  const [emailProvider, setEmailProvider] = useState<string>("");
  const [isResending, setIsResending] = useState<boolean>(false);
  const [resendMessage, setResendMessage] = useState<string>("");
  const [resendMessageType, setResendMessageType] = useState<"success" | "error" | "">("");

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const email = urlParams.get('email') || '';
    setUserEmail(email);

    if (email) {
      const domain = email.split('@')[1]?.toLowerCase();
      switch (domain) {
        case 'gmail.com':
          setEmailProvider('gmail');
          break;
        case 'outlook.com':
        case 'hotmail.com':
        case 'live.com':
          setEmailProvider('outlook');
          break;
        case 'yahoo.com':
          setEmailProvider('yahoo');
          break;
        default:
          setEmailProvider('');
      }
    }
  }, [location]);

  const getEmailProviderUrl = () => {
    switch (emailProvider) {
      case 'gmail':
        return 'https://mail.google.com';
      case 'outlook':
        return 'https://outlook.live.com';
      case 'yahoo':
        return 'https://mail.yahoo.com';
      default:
        return '';
    }
  };

  const getEmailProviderName = () => {
    switch (emailProvider) {
      case 'gmail':
        return 'Gmail';
      case 'outlook':
        return 'Outlook';
      case 'yahoo':
        return 'Yahoo Mail';
      default:
        return 'your email';
    }
  };

  const handleOpenEmailProvider = () => {
    const url = getEmailProviderUrl();
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleResendEmail = async () => {
    if (!config?.authServiceUrl || !userEmail) {
      setResendMessage("Unable to resend email. Please try again later.");
      setResendMessageType("error");
      return;
    }

    setIsResending(true);
    setResendMessage("");
    setResendMessageType("");

    try {
      const response = await executeResendVerification(config.authServiceUrl, userEmail);

      if (response.success) {
        setResendMessage("Verification email has been resent. Please check your inbox.");
        setResendMessageType("success");
      } else {
        setResendMessage(response.message || "Failed to resend email. Please try again.");
        setResendMessageType("error");
      }
    } catch (error: any) {
      setResendMessage(error.message || "An error occurred. Please try again.");
      setResendMessageType("error");
    } finally {
      setIsResending(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Container fluid className="fullscreenFixed p-0">
      <Row className="h-100 g-0">
        <AuthLeftPanel
          title="Welcome to ViewSpark!"
          subtitle="Your charity management platform"
          showImage={false}
        >
          <div className={s.successIcon}>
            <div className={s.checkmarkCircle}>
              <div className={s.checkmark}>✓</div>
            </div>
          </div>
        </AuthLeftPanel>

        <AuthContentCol className="d-flex flex-column align-items-center justify-content-center bg-white py-3 py-md-4">
          <PageTransition className="d-flex flex-column justify-content-center">
            <MobileLogo />
            <div
              className="w-100 px-3 px-md-4 px-lg-5 mx-auto"
              style={{ maxWidth: "750px" }}
            >
              <div className="text-center">
                <div className="mb-3 mb-md-4">
                  <h1 className={s.title}>Complete Your Registration</h1>
                  <p className={`${s.subtitle} mb-0`}>
                    A verification link has been sent to{" "}
                    {userEmail && <strong>{userEmail}</strong>}
                  </p>
                  {resendMessage && (
                    <div
                      className={`mt-2 ${
                        resendMessageType === "success"
                          ? s.successMessage
                          : s.errorMessage
                      }`}
                    >
                      {resendMessage}
                    </div>
                  )}
                </div>

                <div className={`${s.instructionsCard} mb-2 mb-md-3`}>
                  <div className={s.stepNumber}>1</div>
                  <div className={s.stepContent}>
                    <h3 className={`${s.stepTitle} mb-1`}>
                      Find our verification email
                    </h3>
                    <p className={`${s.stepDescription} mb-0`}>
                      We sent an email from noreply@viewspark.com with your
                      activation link
                    </p>
                  </div>
                </div>

                <div className={`${s.instructionsCard} mb-2 mb-md-3`}>
                  <div className={s.stepNumber}>2</div>
                  <div className={s.stepContent}>
                    <h3 className={`${s.stepTitle} mb-1`}>
                      Activate your account
                    </h3>
                    <p className={`${s.stepDescription} mb-0`}>
                      Click the secure link in the email to verify your identity
                      and enable full access
                    </p>
                  </div>
                </div>

                <div className={`${s.instructionsCard} mb-3 mb-md-4`}>
                  <div className={s.stepNumber}>3</div>
                  <div className={s.stepContent}>
                    <h3 className={`${s.stepTitle} mb-1`}>
                      Begin your fundraising journey
                    </h3>
                    <p className={`${s.stepDescription} mb-0`}>
                      Access powerful tools to manage donors, create campaigns,
                      and track your impact
                    </p>
                  </div>
                </div>

                <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center mb-2 mb-md-3">
                  {emailProvider && (
                    <PrimaryButton
                      onClick={handleOpenEmailProvider}
                      className={`${s.emailButton} flex-fill flex-sm-grow-0`}
                      style={{ minWidth: "200px" }}
                    >
                      <i className={`fa-solid fa-envelope ${s.buttonIcon}`}></i>
                      Check {getEmailProviderName()}
                    </PrimaryButton>
                  )}

                  <PrimaryButton
                    onClick={handleBackToLogin}
                    variant="secondary"
                    className="flex-fill flex-sm-grow-0"
                    style={{ minWidth: "160px" }}
                  >
                    Return to Login
                  </PrimaryButton>
                </div>

                <div className="border-top pt-2 pt-md-3 mt-1 mt-md-2">
                  <p className={`${s.helpTitle} mb-2`}>Need assistance?</p>
                  <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 mb-2">
                    <button
                      className={s.linkButton}
                      onClick={handleResendEmail}
                      disabled={isResending}
                    >
                      {isResending ? "Resending..." : "Resend verification"}
                    </button>
                    <span className={s.helpSeparator}>•</span>
                    <button
                      className={s.linkButton}
                      onClick={() =>
                        (window.location.href = "mailto:support@viewspark.com")
                      }
                    >
                      Contact support
                    </button>
                  </div>
                  <p className={`${s.helpNote} mb-0`}>
                    Most emails arrive within 2 minutes. Please check your spam
                    folder or promotions tab.
                  </p>
                </div>
              </div>
            </div>
          </PageTransition>
        </AuthContentCol>
      </Row>
    </Container>
  );
};

export default withRouter(serviceCaller(withStyles(s)(EmailSentPage)));
